import { type KeyboardEvent, useCallback } from 'react';
import type { SearchBase } from './use-search';
import type { SearchActions } from './use-search-actions';

function optionElementsIn(
    container: HTMLDivElement | null,
): HTMLButtonElement[] {
    return Array.from(
        container?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [],
    );
}

export interface SearchKeys {
    onInputKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
    onOptionKeyDown: (
        e: KeyboardEvent<HTMLButtonElement>,
        activate: () => void,
    ) => void;
}

export function useSearchKeys<T>(
    base: SearchBase<T>,
    actions: SearchActions<T>,
): SearchKeys {
    const {
        containerRef,
        inputRef,
        inputValue,
        pendingColumn,
        dropdown,
        setDropdown,
        removeLastChip,
    } = base;
    const { commitText, cancelPending } = actions;

    const onInputKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                commitText();
                return;
            }
            if (e.key === 'Escape') {
                pendingColumn ? cancelPending() : setDropdown(null);
                return;
            }
            if (e.key === 'Backspace' && !inputValue) {
                pendingColumn ? cancelPending() : removeLastChip();
                return;
            }
            if (e.key === 'ArrowDown' && dropdown) {
                e.preventDefault();
                optionElementsIn(containerRef.current)[0]?.focus();
            }
        },
        [
            commitText,
            cancelPending,
            setDropdown,
            inputValue,
            pendingColumn,
            dropdown,
            containerRef,
            removeLastChip,
        ],
    );

    const onOptionKeyDown = useCallback(
        (e: KeyboardEvent<HTMLButtonElement>, activate: () => void) => {
            const options = optionElementsIn(containerRef.current);
            const idx = options.indexOf(e.currentTarget);
            if (e.key === 'Enter') {
                e.preventDefault();
                activate();
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                pendingColumn ? cancelPending() : setDropdown(null);
                if (!pendingColumn) inputRef.current?.focus();
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                options[idx + 1]?.focus();
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                (idx === 0 ? inputRef.current : options[idx - 1])?.focus();
            }
        },
        [containerRef, inputRef, pendingColumn, cancelPending, setDropdown],
    );

    return { onInputKeyDown, onOptionKeyDown };
}
